// src/schedule/components/ColorPickerPopover.jsx
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function ColorPickerPopover({
    anchorEl,
    open,
    value = "#888888",
    onChange,
    onCancel,
    onSave,
    width = 320,
    offset = 10,
    zIndex = 2147483648, // Wyższy niż powiększona karta
}) {
    const panelRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const computePosition = useCallback(() => {
        if (!anchorEl || !panelRef.current) return;

        const ar = anchorEl.getBoundingClientRect();
        const pw = Math.min(panelRef.current.offsetWidth || width, window.innerWidth * 0.92);
        const ph = Math.min(panelRef.current.offsetHeight || 220, window.innerHeight * 0.8);

        // 1) spróbuj po PRAWEJ
        let left = ar.right + offset;
        let top = ar.top;

        const fitsRight = left + pw <= window.innerWidth - 8;
        const fitsLeft = (ar.left - offset - pw) >= 8;

        if (!fitsRight) {
            // 2) spróbuj po LEWEJ
            if (fitsLeft) {
                left = ar.left - offset - pw;
            } else {
                // 3) pod anchor (środek wyrównany do lewej)
                left = Math.max(8, Math.min(ar.left, window.innerWidth - pw - 8));
                top = ar.bottom + offset;
            }
        }

        // pionowe dopasowanie: jeśli się nie mieści na dole – spróbuj nad
        if (top + ph > window.innerHeight - 8) {
            const alt = ar.top - ph - offset;
            if (alt >= 8) top = alt;
        }

        // ostateczny clamp
        left = Math.min(Math.max(8, left), window.innerWidth - pw - 8);
        top = Math.min(Math.max(8, top), window.innerHeight - ph - 8);

        setPos({ top, left });
    }, [anchorEl, width, offset]);

    useLayoutEffect(() => {
        if (!open || !anchorEl) return;
        computePosition();
        const id = requestAnimationFrame(computePosition);
        return () => cancelAnimationFrame(id);
    }, [open, anchorEl, value, computePosition]);

    useEffect(() => {
        if (!open) return;

        const re = () => computePosition();
        window.addEventListener("resize", re, { passive: true });
        window.addEventListener("scroll", re, { passive: true });

        const onKey = (e) => e.key === "Escape" && onCancel?.();
        window.addEventListener("keydown", onKey, true);

        const onDoc = (e) => {
            const panel = panelRef.current;
            if (!panel || !anchorEl) return;
            if (!panel.contains(e.target) && !anchorEl.contains(e.target)) onCancel?.();
        };
        document.addEventListener("mousedown", onDoc, true);
        document.addEventListener("touchstart", onDoc, true);

        return () => {
            window.removeEventListener("resize", re);
            window.removeEventListener("scroll", re);
            window.removeEventListener("keydown", onKey, true);
            document.removeEventListener("mousedown", onDoc, true);
            document.removeEventListener("touchstart", onDoc, true);
        };
    }, [open, anchorEl, onCancel, computePosition]);

    if (!open || !anchorEl) return null;

    const body = (
        <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                zIndex,
                width: Math.min(width, window.innerWidth * 0.92),
                maxWidth: "92vw",
                maxHeight: "80vh",
                overflow: "auto",
            }}
            className="rounded-xl border border-gray-700 bg-gray-800 shadow-2xl p-3 select-none"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-gray-200">Kolor wydarzenia</div>
                <button type="button" className="text-gray-400 hover:text-gray-200 px-2" onClick={onCancel}>✕</button>
            </div>

            <div className="flex items-center gap-2 mb-3">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    className="w-12 h-10 bg-transparent border border-gray-700 rounded overflow-hidden"
                    aria-label="Wybierz kolor"
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-gray-100 font-mono"
                    spellCheck={false}
                />
            </div>

            {/* większa paleta – wygodna na mobile */}
            <div className="grid grid-cols-8 gap-1 mb-3">
                {[
                    "#EF4444", "#F97316", "#F59E0B", "#EAB308", "#84CC16", "#10B981", "#06B6D4", "#3B82F6",
                    "#6366F1", "#8B5CF6", "#A855F7", "#EC4899", "#F43F5E", "#6B7280", "#9CA3AF", "#111827"
                ].map((c) => (
                    <button
                        key={c}
                        type="button"
                        className="h-7 rounded border border-gray-700"
                        style={{ background: c }}
                        title={c}
                        onClick={() => onChange?.(c)}
                    />
                ))}
            </div>

            <div className="flex gap-2">
                <button type="button" onClick={onSave} className="w-1/2 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500">
                    Zapisz
                </button>
                <button type="button" onClick={onCancel} className="w-1/2 px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600">
                    Anuluj
                </button>
            </div>
        </div>
    );

    return createPortal(body, document.body);
}
